import { useCallback, useEffect, useRef, useState } from 'react';

import { isAxiosError } from 'axios';

import { DrawingDocument } from '../new-project-cad/drawingDocument';
import { drawingsApi } from './drawingsApi';

export type DrawingSaveState = 'saved' | 'pending' | 'saving' | 'error' | 'conflict';

const AUTOSAVE_DELAY_MS = 1200;

/**
 * Keeps a drawing saved on the server. A change is saved shortly after the last edit, one save at a
 * time, each based on the lock key the previous one got back. When the drawing was saved from
 * somewhere else in between, that is reported as a conflict instead of overwritten.
 *
 * `build` returns the drawing as it is now. The first `changed()` marks the drawing as it was opened.
 * With `auto` off nothing is saved until `save()` is called; leaving with changes still warns.
 *
 * A new drawing (`drawingId` null) exists only here until its first save, which `create`s it; the
 * first change is then saved rather than taken as the drawing as it was opened.
 */
export function useDrawingAutosave(
  drawingId: number | null,
  initialLockKey: number,
  build: () => DrawingDocument | null,
  {
    auto = true,
    create,
  }: { auto?: boolean; create?: (doc: DrawingDocument) => Promise<{ id: number; lockKey: number }> } = {},
) {
  const [state, setState] = useState<DrawingSaveState>('saved');
  const id = useRef(drawingId);
  const lockKey = useRef(initialLockKey);
  const everSaved = useRef(false);
  const savedJson = useRef<string | null>(drawingId === null ? '' : null);
  const createRef = useRef(create);
  createRef.current = create;
  const changedSinceSave = useRef(false);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const conflict = useRef(false);
  const released = useRef(false);
  const buildRef = useRef(build);
  buildRef.current = build;

  /** Saves the plan if it changed (or always, with `force`); resolves to whether it is saved. */
  const save = useCallback(async (force = false): Promise<boolean> => {
    clearTimeout(timer.current);
    while (inFlight.current) await inFlight.current;
    if (conflict.current) return false;
    const doc = buildRef.current();
    if (!doc) return true;
    const json = JSON.stringify(doc);
    if (!force && json === savedJson.current) {
      changedSinceSave.current = false;
      setState('saved');
      return true;
    }
    changedSinceSave.current = false;
    setState('saving');
    const saving =
      id.current !== null
        ? drawingsApi.save(id.current, doc, lockKey.current)
        : createRef.current
          ? createRef.current(doc).then((created) => {
              id.current = created.id;
              return created;
            })
          : null;
    if (!saving) {
      setState('saved');
      return true;
    }
    const run = saving
      .then((response) => {
        lockKey.current = response.lockKey;
        savedJson.current = json;
        everSaved.current = true;
        setState(changedSinceSave.current ? 'pending' : 'saved');
        return true;
      })
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 409) {
          conflict.current = true;
          setState('conflict');
        } else {
          setState('error');
        }
        return false;
      })
      .finally(() => {
        inFlight.current = null;
      });
    inFlight.current = run;
    return run;
  }, []);

  const changed = useCallback(() => {
    if (savedJson.current === null) {
      savedJson.current = JSON.stringify(buildRef.current());
      return;
    }
    if (conflict.current) return;
    changedSinceSave.current = true;
    setState((current) => (current === 'saving' ? current : 'pending'));
    if (!auto) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(), AUTOSAVE_DELAY_MS);
  }, [save, auto]);

  const unsaved = state === 'pending' || state === 'saving' || state === 'error';
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      if (!released.current) event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  // Leaving the editor saves what is left.
  useEffect(
    () => () => {
      if (auto && changedSinceSave.current) void save();
    },
    [save, auto],
  );

  // `saved()`: whether this editor has saved the drawing at least once. `release()`: the changes are
  // taken care of (the page is being left on purpose), so leaving no longer warns.
  return {
    state,
    changed,
    save,
    saved: () => everSaved.current,
    release: () => {
      released.current = true;
    },
  };
}
